import { useCallback } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { Grid } from '@simplywallst/ui-core';

import { ROUTE_MARKET_SECTOR } from '@/constants/routes';

import Tooltip from '@/components/Tooltip';
import PNV from '@/components/PositiveNegativeValue';

import type { BestValuationRatio } from '@/utilities/getBestValuationRatio';

import { CellLink } from '@/pages/Stocks/components/DataTable';
import { ValuationRatio } from '@/pages/Stocks/components/StockTable/ValuationRatio';
import { SSRLink } from '@/pages/Stocks/components/StockTable/Row';

import {
  CompanyName,
  PELabel,
  SnowflakeColumn,
  ToolTipText,
  ValueColumn,
} from '@/pages/Stocks/components/StockTable/styled';

import useIndustryIdGetter from '../../hooks/useIndustryIdGetter';

import {
  MarketCap,
  NameColumnStyled,
  SectorColumn,
  DisabledSectorLink,
  SharePrice,
} from './styled';

const Snowflake = loadable(() => import('./Snowflake'), {
  resolveComponent: (components) => components.Snowflake,
});

interface Props {
  companyId: string;
  tickerSymbol: string;
  uniqueSymbol: string;
  snowflakePoints: [number, number, number, number, number];
  shortName: string;
  canonicalUrl: string;
  shortDescription: string;
  return7D: number | null;
  return7DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  marketCapMovement7DFormatted: string;
  bestValuationRatio: BestValuationRatio;
  peFormatted: string;
  pbFormatted: string;
  psFormatted: string;
  sectorSlug: string;
  sector: string;
  sectorId: number | null;
  isSticky: boolean;
  market: string;
  isMarketPage: boolean;
  lastSharePriceFormatted: string;
  lastSharePrice: number | null;
}

const Row = ({
  snowflakePoints,
  canonicalUrl,
  tickerSymbol,
  shortDescription,
  companyId,
  shortName,
  return7D,
  return7DFormatted,
  return1YrAbs,
  return1YrAbsFormatted,
  bestValuationRatio,
  peFormatted,
  pbFormatted,
  psFormatted,
  sector,
  sectorId,
  marketCapMovement7DFormatted,
  isSticky,
  market,
  uniqueSymbol,
  isMarketPage,
  sectorSlug,
  lastSharePriceFormatted,
  lastSharePrice,
}: Props) => {
  const { trackEvent } = useTracking();
  const currentSector = useIndustryIdGetter(sectorId!, market);
  const muteEvent = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  return (
    <tr
      data-cy-id="market-mover-table-row"
      onClick={() =>
        trackEvent({
          action: 'click',
          subject: 'market-mover-table-row',
          uniqueSymbol: uniqueSymbol,
          companyId: companyId,
        })
      }
    >
      <SnowflakeColumn>
        <Grid placeContent="center">
          <Snowflake points={snowflakePoints} />
        </Grid>
      </SnowflakeColumn>
      <NameColumnStyled isSticky={isSticky}>
        <CellLink to={canonicalUrl}>
          <b>{tickerSymbol} </b>
          <Tooltip
            title={<ToolTipText>{shortDescription}</ToolTipText>}
            key={`table-tooltip-description${companyId}`}
            light
            zIndex={99999}
          >
            <CompanyName onTouchEnd={muteEvent} onClick={muteEvent}>
              {shortName}
            </CompanyName>
          </Tooltip>
        </CellLink>
      </NameColumnStyled>
      <ValueColumn value={lastSharePrice}>
        <SSRLink url={canonicalUrl}>
          <SharePrice>{lastSharePriceFormatted}</SharePrice>
        </SSRLink>
      </ValueColumn>
      <PNV as="td" value={return7D}>
        <SSRLink url={canonicalUrl}>
          {return7DFormatted}
          <br />
          <MarketCap>
            {return7D && return7D > 0 ? '+' : ''}
            {marketCapMovement7DFormatted}
          </MarketCap>
        </SSRLink>
      </PNV>
      <PNV as="td" value={return1YrAbs}>
        <SSRLink url={canonicalUrl}>{return1YrAbsFormatted}</SSRLink>
      </PNV>
      <ValueColumn value={bestValuationRatio.valuation}>
        <SSRLink url={canonicalUrl}>
          {!!bestValuationRatio.valuation && (
            <Tooltip
              key={`table-tooltip-${companyId}`}
              title={
                <ValuationRatio
                  priceToEarnings={peFormatted}
                  priceToBook={pbFormatted}
                  priceToSales={psFormatted}
                />
              }
              light
              zIndex={99999}
            >
              <PELabel onTouchEnd={muteEvent} onClick={muteEvent}>
                {bestValuationRatio.valuationType}
              </PELabel>
            </Tooltip>
          )}
          {bestValuationRatio.valuationFormatted}
        </SSRLink>
      </ValueColumn>
      {isMarketPage && (
        <SectorColumn>
          {currentSector ? (
            <CellLink
              to={{
                type: ROUTE_MARKET_SECTOR,
                payload: {
                  market: market,
                  sector: sectorSlug,
                },
              }}
              onClick={(e) => {
                e.stopPropagation(); // prevent doubling up on the tracking event on the <tr/>
                trackEvent({
                  action: 'click',
                  subject: 'market-mover-table-row-sector-column',
                  uniqueSymbol: uniqueSymbol,
                  companyId: companyId,
                });
              }}
            >
              <span>{sector}</span>
            </CellLink>
          ) : (
            <DisabledSectorLink>{sector}</DisabledSectorLink>
          )}
        </SectorColumn>
      )}
    </tr>
  );
};

export default Row;
